import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowDown } from 'lucide-react';

const STEPS = [
  {
    n: '1',
    title: 'Powerfuel kunne ikke længere levere',
    body: 'Powerfuel kan ikke længere levere strøm til din adresse. Derfor fordelte Energinet via DataHub deres kunder til nye elhandelsvirksomheder.',
  },
  {
    n: '2',
    title: 'Du blev tildelt enkel energi',
    body: 'Vi overtog ansvaret for din strøm uden afbrydelse. Du behøvede ikke gøre noget – og din strøm har på intet tidspunkt været i fare.',
  },
  {
    n: '3',
    title: 'Du er placeret på en lovbestemt overgangsaftale',
    body: 'Du er placeret på en reguleret overgangsaftale med et tillæg fastsat af Forsyningstilsynet (§ 72 f). Vi afregner efter dit faktiske forbrug, så du slipper for aconto. Du kan beholde den – eller skifte, når du vil.',
  },
];

export default function PowerfuelProduct() {
  return (
    <section id="powerfuel-flow" className="py-16 md:py-20 bg-[#f5f3ee] scroll-mt-20">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">

          <div className="text-center mb-10">
            <span className="inline-block bg-[#1a2e1a] text-white text-xs font-bold px-3 py-1 rounded-full mb-3">SÅDAN HÆNGER DET SAMMEN</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a2e1a] mb-3" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
              Det er sket – og det er aftalen, du er placeret på
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Ved et tvunget leverandørskift er vi <strong>ifølge loven</strong> forpligtet til at placere dig på en reguleret
              overgangsaftale. Det er ikke et produkt, vi selv har valgt prisen på – tillægget fastsættes af Forsyningstilsynet.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-12">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-white rounded-2xl border border-[#e8e4d9] p-6">
                <div className="w-9 h-9 bg-[#1a2e1a] rounded-full flex items-center justify-center mb-3">
                  <span className="text-[#eab308] font-bold text-sm">{s.n}</span>
                </div>
                <h3 className="font-bold text-[#1a2e1a] text-sm mb-1.5">{s.title}</h3>
                <p className="text-gray-600 text-xs leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border-2 border-[#1a2e1a] shadow-lg p-8 mb-6 max-w-lg mx-auto">
            <div className="text-center mb-6">
              <span className="bg-[#1a2e1a] text-white text-xs font-bold px-3 py-1 rounded-full">LOVBESTEMT OVERGANGSAFTALE</span>
            </div>
            <h3 className="text-2xl font-bold text-[#1a2e1a] text-center mb-2" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>enkel overgangsaftale</h3>
            <p className="text-center text-gray-500 text-sm mb-6">Spotpris + lovbestemt tillæg (§ 72 f) · med forbrugsafregning</p>

            <div className="text-center mb-2">
              <span className="text-4xl font-bold text-[#1a2e1a]">0</span>
              <span className="text-lg text-gray-600 ml-1">kr./md. i abonnement</span>
            </div>
            <div className="text-center mb-6">
              <span className="inline-block bg-[#f5f3ee] border border-[#e8e4d9] rounded-lg px-4 py-2 text-sm text-[#1a2e1a] font-semibold">
                + spotpris + tillæg på 17,5 øre/kWh inkl. moms
              </span>
              <p className="text-xs text-gray-500 mt-2">
                Tillægget er fastsat af Forsyningstilsynet for 2026: <strong>14,08 øre/kWh i DK1</strong> og <strong>14,01 øre/kWh i DK2</strong> (ekskl. moms) – ca. <strong>17,5 øre/kWh inkl. moms</strong>. Se Forsyningstilsynets afgørelse i deres{' '}
                <a href="https://afg.forsyningstilsynet.dk/h/42c520c9-70bc-4643-93f3-3f63bb755d28/e450a70cfd3946d593a6d2ba1257341a?showExact=true" target="_blank" rel="noopener noreferrer" className="underline">afgørelsesdatabase</a>.
              </p>
            </div>

            <div className="space-y-3">
              {[
                '0 kr./md. i abonnement',
                'Forbrugsafregning – du betaler bagud for det, du faktisk har brugt. Ingen aconto/forudbetaling',
                'Spotpris i dit prisområde (DK1/DK2)',
                'Lovbestemt tillæg fastsat af Forsyningstilsynet (§ 72 f)',
                'Net- og systemtariffer, elafgift og moms oven i prisen',
                'Du kan opsige med 1 måneds varsel til udgangen af en måned',
                'Aftalen er bindende for enkel energi i 3 måneder',
              ].map((feature, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#1a2e1a] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{feature}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-[#1a2e1a]/5 border border-[#1a2e1a]/15 rounded-xl p-4">
              <p className="text-sm text-[#1a2e1a] leading-relaxed">
                <strong>Vi har givet dig forbrugsafregning fra dag ét.</strong> Loven kræver kun, at vi placerer dig på
                overgangsaftalen – men vi har valgt at afregne den efter dit faktiske forbrug, så du <strong>ikke</strong> skal
                lægge ny aconto oven i det, du måske allerede har udestående hos Powerfuel. Du betaler bagud for strømmen,
                når du har brugt den.
              </p>
            </div>

            <p className="text-xs text-gray-500 mt-6 leading-relaxed text-center">
              Se fuld prisberegning, betalingsvilkår og alle gebyrer i din leveringsaftale og i vores{' '}
              <Link to="/leveringsbetingelser" className="underline">leveringsbetingelser</Link>. Tillægget er inkl. moms.
            </p>
          </div>

          <div className="flex flex-col items-center mt-10 text-[#1a2e1a]">
            <p className="text-sm font-semibold mb-2" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Vil du spare? Se hvor meget – og skift på et minut</p>
            <ArrowDown className="w-6 h-6 animate-bounce" />
          </div>

        </div>
      </div>
    </section>
  );
}